import { ReloadOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type ITag } from '@mahawi/eshop-common/src/types';
import { Button, Divider, Space, Tag } from 'antd';
import { type ColumnType } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Table from '../components/table';
import TagAdd from '../components/tags/tag-add';
import UpdatedAt from '../components/updated-at';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { tagsLoadAll } from '../reducers/tags/reducer';
import { SPACING } from '../theme/tokens';

interface DataType {
  key: string;
  name: string;
  color: string;
}

function SettingsTagsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const tagsTags = useSelector((s: RootState) => s.Tags.tags);
  const tagsUpdatedAt = useSelector((s: RootState) => s.Tags.updatedAt);
  const tagsInProcess = useSelector((s: RootState) => s.Tags.inProcess);

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnType<object>[]>([]);

  useEffect((): void => {
    const columnsUE: ColumnType<object>[] = [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        onFilter: (value: string, record: DataType): boolean =>
          record.name === value,
        width: '100%',
        render: (_text: string, record: DataType): React.ReactElement => (
          <Tag
            key={record.name}
            style={{
              backgroundColor: record.color,
              borderColor: record.color,
            }}
          >
            {record.name}
          </Tag>
        ),
      },
    ];

    if (!tagsTags) {
      setDataSource([]);

      return;
    }

    const dataSourceUE: DataType[] = tagsTags.map((tag: ITag): DataType => ({
      key: tag.name,
      name: tag.name,
      color: tag.color,
    }));

    setDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [tagsTags, tagsUpdatedAt]);

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader title="Tags" />

      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Button
          type={!tagsTags ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(tagsLoadAll());
          }}
          disabled={tagsInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={tagsUpdatedAt} />
      </Space>

      <TagAdd />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={tagsInProcess && !tagsTags}
      />
    </Space>
  );
}

export default SettingsTagsContainer;
